import { useRef, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { isApiError } from "@bw/shared/api";
import { validateReturnUrl } from "@bw/shared/utils";
import type { UserProfileResponse } from "@bw/shared/types/generated";
import { useMyProfile } from "../../../common/query/useMyProfile";
import { ConsentStep } from "../components/ConsentStep";
import { ProfileStep } from "../components/ProfileStep";
import { AgeOutcome } from "../components/AgeOutcome";
import { useCreateProfile } from "../hooks/useCreateProfile";
import type { ProfileCreateBody } from "../schemas";

/**
 * OnboardingPage — the standalone, centered
 * profile-setup wizard (own crest, no app nav — mirrors the login layout).
 *
 * It composes the legal-consent affirmation + the RHF profile form + the
 * post-create age outcome. It owns:
 *   - the lifted consent `accepted` state — the submit button is GATED on it
 *     (an explicit affirmation, never implicit);
 *   - an explicit, non-persistent acknowledgement that the linked prelaunch
 *     drafts were reviewed. Drafts are deliberately NOT sent to the legal
 *     acceptance API and cannot satisfy a future acceptance gate;
 *   - profile error branching (inline message; stays on /onboarding);
 *   - success branching: an ACTIVE account is routed into the app ("/"); a
 *     pending_parental_consent account is NOT redirected — it shows the honest
 *     parent-approval panel (no fabricated email send).
 *
 * No business logic: the age decision is read straight off the response. The
 * page never computes age and never echoes the DOB.
 */
export function OnboardingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const createProfile = useCreateProfile();

  // #1626: where to land after onboarding. RequireProfile forwards the page the
  // user was originally trying to reach (e.g. a parent's /family/approvals) as
  // ?returnUrl; honor it instead of hardcoding "/". validateReturnUrl rejects
  // open-redirect payloads and defaults to "/" when absent/unsafe.
  const returnTo = validateReturnUrl(searchParams.get("returnUrl"));
  // The SAME profile-existence read RequireProfile uses (one cache entry). A
  // user who ALREADY has a profile must not see the onboarding form — they get
  // a 409 on submit. The inverse guard below bounces them to the app home. A
  // no-profile user surfaces a thrown 404 here (data undefined) → the form shows.
  const { data: existingProfile } = useMyProfile(true);
  const [outcome, setOutcome] = useState<UserProfileResponse | null>(null);
  const [accepted, setAccepted] = useState(false);
  // State-driven disabling follows on the next render. This synchronous latch
  // also rejects a second submit event in the intervening frame.
  const submitInFlightRef = useRef(false);

  // A RETURNING under-13/pending child (#1627): their profile already EXISTS with
  // account_status === "pending_parental_consent" (requires_parental_consent), but
  // there is no in-page `outcome` this session. They must NOT see the create form
  // again (re-submitting 409s and hides their real status) — show the gated
  // AgeOutcome → GuardianLinkStep, which reads the SERVER link status (pending /
  // active / request-a-link). COPPA stays fail-closed: RequireProfile still owns
  // app access; this only re-orients the child off the form. Driven entirely by
  // the real server profile — no fabricated state.
  // Key off account_status — the single source of truth RequireProfile also gates
  // on — NOT requires_parental_consent. They move in lockstep server-side, but an
  // active-but-still-flagged edge response must redirect into the app (RequireProfile
  // owns it), never strand on a no-nav affirmation here.
  const returningPending =
    !outcome &&
    existingProfile != null &&
    existingProfile.account_status === "pending_parental_consent";

  // Already-profiled user landed on /onboarding (navigation or refresh) → send
  // them to the app, NOT the form. The `!outcome` guard is load-bearing: after
  // an in-page create of a PENDING (under-13) account, `outcome` is set AND the
  // create invalidated this query (so `existingProfile` becomes the new 200) —
  // we must still show the AgeOutcome panel, never redirect it away. For an
  // ACTIVE in-page create OnboardingPage already navigates to "/", the same
  // destination, so there is no conflict. A returning PENDING child is the one
  // existing profile we keep on the page (gated panel below), never redirect.
  if (existingProfile && !outcome && !returningPending) {
    return <Navigate to={returnTo} replace />;
  }

  // The gated panel source: the just-created in-page outcome, OR a returning
  // pending child's existing server profile. Either way it's the gated
  // AgeOutcome, never the create form.
  const gatedProfile = outcome ?? (returningPending ? existingProfile : null);

  const handleSubmit = async (body: ProfileCreateBody) => {
    // Defensive: never proceed without the explicit affirmation (the submit
    // button is already gated on `accepted`, but never trust the gate alone).
    if (!accepted || submitInFlightRef.current) return;

    submitInFlightRef.current = true;
    // The linked legal documents are nonbinding prelaunch drafts. Do not call
    // the acceptance endpoint here: an authorized document will have its own
    // exact-version acceptance flow before it can satisfy a legal gate.
    createProfile.mutate(body, {
      onSuccess: (profile) => {
        // COPPA gate FAILS CLOSED: only an EXPLICITLY active account is routed
        // into the app. Anything else — pending_parental_consent, or an
        // ambiguous response that omits the status fields entirely — keeps the
        // possible minor OUT of the app and shows the in-place pending panel.
        // The safe default is "paused", never "let in".
        const pending = profile.account_status !== "active";
        if (pending) {
          setOutcome(profile);
          return;
        }
        // Active: into the app — back to the ?returnUrl destination if the user
        // came in headed somewhere specific (e.g. /family/approvals), else "/".
        // RequireProfile now re-reads a fresh 200 because the mutation
        // invalidated the profile query key.
        navigate(returnTo, { replace: true });
      },
      onSettled: () => {
        submitInFlightRef.current = false;
      },
    });
  };

  const profileErrorMessage = createProfile.isError
    ? isApiError(createProfile.error)
      ? createProfile.error.status === 409
        ? "You already have a profile. Try refreshing the page."
        : createProfile.error.message ||
          "Something went wrong. Please try again."
      : "Something went wrong. Please try again."
    : null;

  const errorMessage = profileErrorMessage;

  return (
    <Box
      component="main"
      sx={{
        minHeight: "100dvh",
        display: "grid",
        alignItems: "center",
        bgcolor: "background.default",
        py: { xs: 3, sm: 6 },
      }}
    >
      <Container maxWidth="sm" sx={{ width: "100%" }}>
        <Stack spacing={3}>
          <Stack component="header" spacing={1}>
            <Typography component="h1" sx={{ fontSize: "1.375rem", fontWeight: 700 }}>
              Create your player profile
            </Typography>
          </Stack>

          {gatedProfile ? (
            <AgeOutcome profile={gatedProfile} />
          ) : (
            <ProfileStep
              onSubmit={handleSubmit}
              submitting={createProfile.isPending}
              consentAccepted={accepted}
              errorSlot={
                errorMessage ? (
                  <Alert severity="error" role="alert">
                    {errorMessage}
                  </Alert>
                ) : null
              }
              consent={
                <ConsentStep
                  accepted={accepted}
                  onAcceptedChange={setAccepted}
                />
              }
            />
          )}
        </Stack>
      </Container>
    </Box>
  );
}
